import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiAlertCircle,
  FiArrowLeft,
  FiCheckCircle,
  FiPause,
  FiPlay,
  FiRefreshCw,
  FiSkipBack,
  FiSkipForward,
  FiVolume2,
} from 'react-icons/fi';
import Sidebar from './Sidebar';
import DashboardHeader from './DashboardHeader';
import SystemPlaybackSpeedControl, { useSystemPlaybackRate } from './SystemPlaybackSpeedControl';
import { useSidebar } from '../contexts/SidebarContext';
import {
  authAPI,
  publicStructuralSegmentsAPI,
  recordingsAPI,
  StructuralSegment,
  subscriptionAPI,
} from '../services/api';
import { canUseSegment } from '../utils/materialAccess';
import { parseExerciseNotes, ExerciseNoteBlock } from '../utils/parseExerciseNotes';
import { applySystemPlaybackRate } from '../utils/systemPlaybackSpeed';
import { BilingualText } from './BilingualText';
import './StructuralSegmentPractice.css';

type PracticePhase = 'idle' | 'playing-prompt' | 'prompt-done' | 'answers';

const PROMPT_SEEK_SECONDS = 5;

function formatAudioTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
}

interface StructuralSegmentPracticeProps {
  onLogout?: () => void;
}

const StructuralSegmentPractice: React.FC<StructuralSegmentPracticeProps> = ({ onLogout }) => {
  const { segmentId } = useParams<{ segmentId: string }>();
  const navigate = useNavigate();
  const { isCollapsed } = useSidebar();
  const [systemPlaybackRate, setSystemPlaybackRate] = useSystemPlaybackRate();

  const [segment, setSegment] = useState<StructuralSegment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<PracticePhase>('idle');
  const [hasSubscription, setHasSubscription] = useState<boolean | null>(null);
  const [userInfo, setUserInfo] = useState({
    email: '',
    organization: 'NAATI',
    is_staff: false,
  });
  const [promptBlobUrl, setPromptBlobUrl] = useState<string | null>(null);
  const [answerBlobUrls, setAnswerBlobUrls] = useState<Map<number, string>>(new Map());
  const [loadingAnswers, setLoadingAnswers] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [promptPlaying, setPromptPlaying] = useState(false);
  const [promptTime, setPromptTime] = useState(0);
  const [promptDuration, setPromptDuration] = useState(0);

  const promptAudioRef = useRef<HTMLAudioElement | null>(null);
  const answerAudioRefs = useRef<Map<number, HTMLAudioElement>>(new Map());

  const sortedAudios = useMemo(() => {
    if (!segment?.audios) return [];
    return [...segment.audios].sort((a, b) => a.order - b.order);
  }, [segment]);

  const promptAudio = sortedAudios[0] ?? null;
  const answerAudios = sortedAudios.slice(1);
  const exerciseNotes = useMemo(
    () => parseExerciseNotes(segment?.description),
    [segment?.description]
  );

  const segmentIdNum = segmentId ? Number(segmentId) : NaN;

  useEffect(() => {
    const init = async () => {
      try {
        const profile = await authAPI.getProfile();
        setUserInfo({
          email: profile.email || '',
          organization: profile.organization || 'NAATI',
          is_staff: profile.is_staff || profile.is_superuser || false,
        });
        const status = await subscriptionAPI.getSubscriptionStatus();
        setHasSubscription(
          Boolean(status.has_subscription || profile.is_staff || profile.is_superuser)
        );
      } catch {
        setHasSubscription(false);
      }
    };
    void init();
  }, []);

  useEffect(() => {
    if (hasSubscription === null || !segmentId || Number.isNaN(segmentIdNum)) return;

    let alive = true;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await publicStructuralSegmentsAPI.getSegment(segmentIdNum);
        if (!alive) return;

        if (!canUseSegment(hasSubscription, userInfo.is_staff, data.is_free_public)) {
          setError('This segment requires an active subscription, or choose one marked as free.');
          setLoading(false);
          return;
        }

        if (data.audios.length < 3) {
          setError('This segment needs at least 3 audio clips: 1 prompt + 2 answer recordings.');
          setLoading(false);
          return;
        }

        setSegment(data);

        try {
          const completion = await publicStructuralSegmentsAPI.checkCompletion(segmentIdNum);
          if (completion.is_completed) setCompleted(true);
        } catch {
          /* ignore */
        }
      } catch (err: unknown) {
        if (!alive) return;
        setError(err instanceof Error ? err.message : 'Failed to load segment');
      } finally {
        if (alive) setLoading(false);
      }
    };

    void load();
    return () => {
      alive = false;
    };
  }, [hasSubscription, segmentId, segmentIdNum, userInfo.is_staff]);

  useEffect(() => {
    if (!promptAudio?.audio_file_data?.id) {
      setPromptBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      return undefined;
    }

    let alive = true;
    const audioId = promptAudio.audio_file_data.id;

    recordingsAPI
      .fetchAudioBlob(audioId)
      .then((url) => {
        if (!alive) {
          URL.revokeObjectURL(url);
          return;
        }
        setPromptBlobUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return url;
        });
      })
      .catch(() => {
        if (alive) setError('Failed to load prompt audio');
      });

    return () => {
      alive = false;
    };
  }, [promptAudio?.audio_file_data?.id]);

  useEffect(() => {
    return () => {
      if (promptBlobUrl) URL.revokeObjectURL(promptBlobUrl);
      answerBlobUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [promptBlobUrl, answerBlobUrls]);

  useEffect(() => {
    applySystemPlaybackRate(promptAudioRef.current, systemPlaybackRate);
    answerAudioRefs.current.forEach((el) => applySystemPlaybackRate(el, systemPlaybackRate));
  }, [systemPlaybackRate, promptBlobUrl, answerBlobUrls, phase]);

  useEffect(() => {
    const audio = promptAudioRef.current;
    if (!audio || !promptBlobUrl) return undefined;

    const syncDuration = () => {
      if (Number.isFinite(audio.duration)) setPromptDuration(audio.duration);
    };
    const onTimeUpdate = () => setPromptTime(audio.currentTime);
    const onPlay = () => setPromptPlaying(true);
    const onPause = () => setPromptPlaying(false);

    syncDuration();
    audio.addEventListener('loadedmetadata', syncDuration);
    audio.addEventListener('durationchange', syncDuration);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);

    return () => {
      audio.removeEventListener('loadedmetadata', syncDuration);
      audio.removeEventListener('durationchange', syncDuration);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
    };
  }, [promptBlobUrl]);

  const loadAnswerBlobs = useCallback(async () => {
    if (answerAudios.length === 0) return;
    setLoadingAnswers(true);
    const next = new Map<number, string>();
    try {
      for (const item of answerAudios) {
        const id = item.audio_file_data?.id;
        if (!id) continue;
        const url = await recordingsAPI.fetchAudioBlob(id);
        next.set(item.id, url);
      }
      setAnswerBlobUrls((prev) => {
        prev.forEach((url) => URL.revokeObjectURL(url));
        return next;
      });
    } catch {
      setError('Failed to load answer audio');
    } finally {
      setLoadingAnswers(false);
    }
  }, [answerAudios]);

  const registerCompletion = useCallback(async () => {
    if (!segment || completing) return;
    setCompleting(true);
    try {
      const clips = sortedAudios.map((_, index) => ({
        audio_index: index,
        attempts: 1,
      }));
      await recordingsAPI.registerPracticeSession({
        segmentId: segment.id,
        dialogueId: null,
        clips,
      });
      setCompleted(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not save progress');
    } finally {
      setCompleting(false);
    }
  }, [segment, completing, sortedAudios]);

  const handleStart = () => {
    const audio = promptAudioRef.current;
    if (!audio || !promptBlobUrl) return;
    setPhase('playing-prompt');
    audio.currentTime = 0;
    applySystemPlaybackRate(audio, systemPlaybackRate);
    void audio.play().catch(() => setError('Could not play audio. Please try again.'));
  };

  const handlePromptEnded = () => {
    setPromptPlaying(false);
    setPhase('prompt-done');
  };

  const togglePromptPlayback = () => {
    const audio = promptAudioRef.current;
    if (!audio || !promptBlobUrl) return;

    if (audio.paused) {
      if (phase === 'prompt-done') {
        setPhase('playing-prompt');
      }
      applySystemPlaybackRate(audio, systemPlaybackRate);
      void audio.play().catch(() => setError('Could not play audio. Please try again.'));
      return;
    }

    audio.pause();
  };

  const seekPromptBy = (seconds: number) => {
    const audio = promptAudioRef.current;
    if (!audio) return;

    const max = Number.isFinite(audio.duration) ? audio.duration : 0;
    audio.currentTime = Math.max(0, Math.min(max, audio.currentTime + seconds));
    setPromptTime(audio.currentTime);

    if (phase === 'prompt-done' && audio.currentTime < max) {
      setPhase('playing-prompt');
    }
  };

  const handlePromptProgressClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const audio = promptAudioRef.current;
    if (!audio || !promptDuration) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    audio.currentTime = ratio * promptDuration;
    setPromptTime(audio.currentTime);

    if (phase === 'prompt-done' && audio.currentTime < promptDuration) {
      setPhase('playing-prompt');
    }
  };

  const handleShowAnswers = async () => {
    if (promptAudioRef.current) {
      promptAudioRef.current.pause();
    }
    await loadAnswerBlobs();
    setPhase('answers');
    void registerCompletion();
  };

  const handlePracticeAgain = () => {
    answerBlobUrls.forEach((url) => URL.revokeObjectURL(url));
    setAnswerBlobUrls(new Map());
    if (promptAudioRef.current) {
      promptAudioRef.current.pause();
      promptAudioRef.current.currentTime = 0;
    }
    answerAudioRefs.current.forEach((el) => {
      el.pause();
      el.currentTime = 0;
    });
    setPromptPlaying(false);
    setPromptTime(0);
    setPromptDuration(0);
    setPhase('idle');
  };

  const promptProgressPercent =
    promptDuration > 0 ? Math.min(100, (promptTime / promptDuration) * 100) : 0;

  if (loading) {
    return (
      <Shell isCollapsed={isCollapsed} userInfo={userInfo} onLogout={onLogout}>
        <div className="ssp-loading">Loading segment…</div>
      </Shell>
    );
  }

  if (error && !segment) {
    return (
      <Shell isCollapsed={isCollapsed} userInfo={userInfo} onLogout={onLogout}>
        <div className="ssp-error">
          {React.createElement(FiAlertCircle as React.ComponentType<{ size?: number }>, { size: 40 })}
          <p>{error}</p>
          <button type="button" className="ssp-btn ssp-btn-secondary" onClick={() => navigate('/material/structured-segments')}>
            Back to segments
          </button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell isCollapsed={isCollapsed} userInfo={userInfo} onLogout={onLogout}>
      <div className="ssp-page">
        <header className="ssp-header">
          <button type="button" className="ssp-back" onClick={() => navigate('/material/structured-segments')}>
            {React.createElement(FiArrowLeft as React.ComponentType<{ size?: number }>, { size: 18 })}
            Back to segments
          </button>
          <div className="ssp-header-main">
            <div>
              <h1 className="ssp-title">{segment?.name}</h1>
              {segment?.test_category && <span className="ssp-category">{segment.test_category}</span>}
            </div>
            <div className="ssp-header-actions">
              <SystemPlaybackSpeedControl value={systemPlaybackRate} onChange={setSystemPlaybackRate} />
              {completed && (
                <span className="ssp-completed-badge">
                  {React.createElement(FiCheckCircle as React.ComponentType<{ size?: number }>, { size: 16 })}
                  Completed
                </span>
              )}
            </div>
          </div>
          {segment?.description && phase !== 'answers' && exerciseNotes.length === 0 && (
            <BilingualText as="p" className="ssp-lead">{segment.description}</BilingualText>
          )}
        </header>

        {error && (
          <div className="ssp-inline-error" role="alert">
            {error}
          </div>
        )}

        <AnimatePresence mode="wait">
          {phase !== 'answers' && (
            <motion.section
              key="prompt-stage"
              className="ssp-prompt-stage"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
            >
              <div className="ssp-prompt-card">
                <div className="ssp-prompt-icon">
                  {React.createElement(FiVolume2 as React.ComponentType<{ size?: number }>, { size: 28 })}
                </div>
                <div className="ssp-prompt-body">
                  <h2>Listen to the segment</h2>
                  <p>
                    Press <strong>Start</strong> to hear the prompt. When it finishes, use <strong>Next</strong> to
                    reveal the model answers and exercise notes.
                  </p>
                  {promptAudio?.audio_file_data?.file_name && (
                    <span className="ssp-audio-label">{promptAudio.audio_file_data.file_name}</span>
                  )}
                </div>
                {promptBlobUrl && (
                  <audio
                    ref={promptAudioRef}
                    src={promptBlobUrl}
                    preload="auto"
                    onEnded={handlePromptEnded}
                    className="ssp-hidden-audio"
                  />
                )}
              </div>

              <div className="ssp-prompt-actions">
                {phase === 'idle' && (
                  <button type="button" className="ssp-btn ssp-btn-primary" onClick={handleStart} disabled={!promptBlobUrl}>
                    {React.createElement(FiPlay as React.ComponentType<{ size?: number }>, { size: 18 })}
                    Start
                  </button>
                )}
                {(phase === 'playing-prompt' || phase === 'prompt-done') && (
                  <div className="ssp-playback-bar" role="group" aria-label="Prompt audio controls">
                    <div className="ssp-playback-controls">
                      <button
                        type="button"
                        className="ssp-playback-btn"
                        onClick={() => seekPromptBy(-PROMPT_SEEK_SECONDS)}
                        aria-label={`Skip back ${PROMPT_SEEK_SECONDS} seconds`}
                      >
                        {React.createElement(FiSkipBack as React.ComponentType<{ size?: number }>, { size: 18 })}
                      </button>
                      <button
                        type="button"
                        className="ssp-playback-btn ssp-playback-btn--primary"
                        onClick={togglePromptPlayback}
                        aria-label={promptPlaying ? 'Pause' : 'Play'}
                      >
                        {promptPlaying
                          ? React.createElement(FiPause as React.ComponentType<{ size?: number }>, { size: 20 })
                          : React.createElement(FiPlay as React.ComponentType<{ size?: number }>, { size: 20 })}
                      </button>
                      <button
                        type="button"
                        className="ssp-playback-btn"
                        onClick={() => seekPromptBy(PROMPT_SEEK_SECONDS)}
                        aria-label={`Skip forward ${PROMPT_SEEK_SECONDS} seconds`}
                      >
                        {React.createElement(FiSkipForward as React.ComponentType<{ size?: number }>, { size: 18 })}
                      </button>
                    </div>
                    <div className="ssp-playback-progress-row">
                      <span className="ssp-playback-time">{formatAudioTime(promptTime)}</span>
                      <div
                        className="ssp-playback-progress"
                        onClick={handlePromptProgressClick}
                        role="slider"
                        aria-label="Playback position"
                        aria-valuemin={0}
                        aria-valuemax={promptDuration}
                        aria-valuenow={promptTime}
                      >
                        <div
                          className="ssp-playback-progress-fill"
                          style={{ width: `${promptProgressPercent}%` }}
                        />
                      </div>
                      <span className="ssp-playback-time">{formatAudioTime(promptDuration)}</span>
                    </div>
                  </div>
                )}
                {phase === 'prompt-done' && (
                  <button type="button" className="ssp-btn ssp-btn-primary" onClick={() => void handleShowAnswers()}>
                    Next — show answers
                  </button>
                )}
              </div>
            </motion.section>
          )}

          {phase === 'answers' && (
            <motion.section
              key="answers-stage"
              className="ssp-answers-stage"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {loadingAnswers ? (
                <div className="ssp-loading ssp-loading--inline">Loading answers…</div>
              ) : (
                <div className="ssp-answers-grid">
                  <div className="ssp-answers-audio-col">
                    <h2 className="ssp-section-title">Model answers</h2>
                    <div className="ssp-answer-list">
                      {answerAudios.map((item, index) => {
                        const blobUrl = answerBlobUrls.get(item.id);
                        const label = item.audio_file_data?.file_name || `Answer ${index + 1}`;
                        return (
                          <div key={item.id} className="ssp-answer-card">
                            <div className="ssp-answer-card-head">
                              <span className="ssp-answer-index">{index + 1}</span>
                              <span className="ssp-answer-name">{label}</span>
                            </div>
                            {blobUrl && (
                              <audio
                                ref={(el) => {
                                  if (el) answerAudioRefs.current.set(item.id, el);
                                  else answerAudioRefs.current.delete(item.id);
                                }}
                                src={blobUrl}
                                preload="auto"
                                controls
                                className="ssp-answer-audio"
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <aside className="ssp-notes-col ssp-bilingual-panel">
                    <h2 className="ssp-section-title">Exercise notes</h2>
                    {exerciseNotes.length > 0 ? (
                      <div className="ssp-notes-list">
                        {exerciseNotes.map((note, i) => (
                          <ExerciseNoteCard key={i} note={note} index={i + 1} />
                        ))}
                      </div>
                    ) : segment?.description ? (
                      <BilingualText as="div" className="ssp-notes-fallback">
                        {segment.description}
                      </BilingualText>
                    ) : (
                      <p className="ssp-notes-empty">No exercise notes for this segment yet.</p>
                    )}
                  </aside>
                </div>
              )}

              <div className="ssp-answers-footer">
                <button type="button" className="ssp-btn ssp-btn-secondary" onClick={handlePracticeAgain}>
                  {React.createElement(FiRefreshCw as React.ComponentType<{ size?: number }>, { size: 16 })}
                  Listen again
                </button>
                <button
                  type="button"
                  className="ssp-btn ssp-btn-primary"
                  onClick={() => navigate('/material/structured-segments')}
                >
                  Back to list
                </button>
              </div>
            </motion.section>
          )}
        </AnimatePresence>
      </div>
    </Shell>
  );
};

function ExerciseNoteCard({ note, index }: { note: ExerciseNoteBlock; index: number }) {
  return (
    <article className="ssp-note-card">
      <header className="ssp-note-card-header">
        <span className="ssp-note-index">{index}</span>
        <BilingualText as="h3">{note.title}</BilingualText>
      </header>
      {note.intro && (
        <BilingualText as="p" className="ssp-note-intro">{note.intro}</BilingualText>
      )}
      {note.structure && (
        <div className="ssp-note-field">
          <span className="ssp-note-label">Structure / ساختار</span>
          <BilingualText as="p">{note.structure}</BilingualText>
        </div>
      )}
      {note.exampleFromText && (
        <div className="ssp-note-field ssp-note-field--example">
          <span className="ssp-note-label">Example from text / مثال از متن</span>
          <BilingualText as="p">{note.exampleFromText}</BilingualText>
        </div>
      )}
      {note.similarExample && (
        <div className="ssp-note-field ssp-note-field--example">
          <span className="ssp-note-label">Similar example / مثال مشابه</span>
          <BilingualText as="p">{note.similarExample}</BilingualText>
        </div>
      )}
      {note.usage && (
        <div className="ssp-note-field ssp-note-field--usage">
          <span className="ssp-note-label">Usage / کاربرد</span>
          <BilingualText as="p">{note.usage}</BilingualText>
        </div>
      )}
    </article>
  );
}

function Shell({
  children,
  isCollapsed,
  userInfo,
  onLogout,
}: {
  children: React.ReactNode;
  isCollapsed: boolean;
  userInfo: { email: string; organization: string };
  onLogout?: () => void;
}) {
  return (
    <div className={`dashboard ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
      <Sidebar />
      <div className="dashboard-main">
        <DashboardHeader
          userEmail={userInfo.email}
          organization={userInfo.organization}
          onLogout={onLogout}
        />
        <div className="dashboard-content">
          <div className="dashboard-content-inner">{children}</div>
        </div>
      </div>
    </div>
  );
}

export default StructuralSegmentPractice;
